import React from "react";
import {
  Box,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

const SYSTEM_FIELDS_CONFIG = [
  { key: "nombre", label: "Nombre del Cliente", required: true },
  { key: "telefono", label: "Teléfono / WhatsApp", required: true },
  { key: "vehiculo_consultado", label: "Vehículo / Consulta", required: true },
  { key: "email", label: "Correo electrónico", required: false },
  { key: "obtenido_de", label: "Origen / Obtenido de", required: false },
  { key: "observacion", label: "Observación / Notas", required: false },
];

export default function CsvColumnMappingStep({
  headers = [],
  mapping = {},
  onMappingChange,
  ambiguousFields = [],
  sampleRows = [],
  validationError = null,
}) {
  // Contar cuántos campos obligatorios faltan
  const missingRequired = SYSTEM_FIELDS_CONFIG.filter(
    (f) => f.required && (!mapping[f.key] || String(mapping[f.key]).trim() === "")
  );

  // Detectar columnas duplicadas
  const usedColumns = {};
  const duplicateColumns = new Set();
  Object.entries(mapping).forEach(([sysField, colName]) => {
    if (colName && String(colName).trim() !== "") {
      const col = String(colName).trim();
      if (usedColumns[col]) {
        duplicateColumns.add(col);
      } else {
        usedColumns[col] = sysField;
      }
    }
  });

  return (
    <Box sx={{ py: 1 }}>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Mapeo de Columnas
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Asociá las columnas de tu archivo con los campos del sistema Vyonta Manager.
        Los campos con asterisco (<strong>*</strong>) son obligatorios.
      </Typography>

      {validationError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {validationError}
        </Alert>
      )}

      {duplicateColumns.size > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Atención: La(s) columna(s){" "}
          <strong>{Array.from(duplicateColumns).join(", ")}</strong> están
          asignadas a más de un campo del sistema. Cada columna solo debe usarse una vez.
        </Alert>
      )}

      {missingRequired.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Falta mapear el/los campo(s) obligatorio(s):{" "}
          <strong>{missingRequired.map((f) => f.label).join(", ")}</strong>.
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {SYSTEM_FIELDS_CONFIG.map((field) => {
          const isAmbiguous = ambiguousFields.includes(field.key);
          const currentValue = mapping[field.key] || "";
          const isDuplicate =
            currentValue && duplicateColumns.has(String(currentValue).trim());

          return (
            <Grid size={{ xs: 12, sm: 6 }} key={field.key}>
              <FormControl
                fullWidth
                size="small"
                error={Boolean(isDuplicate || (field.required && !currentValue))}
              >
                <InputLabel id={`label-${field.key}`}>
                  {field.label} {field.required ? "*" : "(Opcional)"}
                </InputLabel>
                <Select
                  labelId={`label-${field.key}`}
                  value={currentValue}
                  label={`${field.label} ${field.required ? "*" : "(Opcional)"}`}
                  onChange={(e) => onMappingChange(field.key, e.target.value)}
                >
                  <MenuItem value="">
                    <em>-- Ignorar / No mapear --</em>
                  </MenuItem>
                  {headers.map((h) => (
                    <MenuItem key={h} value={h}>
                      {h}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mt: 0.5,
                  minHeight: 24,
                }}
              >
                {isAmbiguous && !currentValue && (
                  <Chip
                    icon={<WarningAmberIcon />}
                    label="Ambigüedad detectada - Seleccionar manualmente"
                    color="warning"
                    size="small"
                    variant="outlined"
                  />
                )}
                {currentValue && !isDuplicate && (
                  <Chip
                    icon={<CheckCircleOutlineIcon />}
                    label={`Asignado a: ${currentValue}`}
                    color="success"
                    size="small"
                    variant="outlined"
                  />
                )}
                {isDuplicate && (
                  <Chip
                    icon={<WarningAmberIcon />}
                    label="Columna duplicada"
                    color="error"
                    size="small"
                  />
                )}
              </Box>
            </Grid>
          );
        })}
      </Grid>

      {sampleRows && sampleRows.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Vista Previa de Filas de Ejemplo ({sampleRows.length} fila(s))
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Usá esta muestra de tu archivo para verificar qué columna contiene cada dato.
          </Typography>

          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 220, mb: 2 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {headers.map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 600, backgroundColor: "#f5f5f5" }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sampleRows.map((row, idx) => (
                  <TableRow key={idx}>
                    {headers.map((h) => (
                      <TableCell key={h}>
                        {row[h] !== undefined && row[h] !== null
                          ? String(row[h])
                          : ""}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ mt: 2 }}>
            Muestra según Mapeo Seleccionado
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Valores originales que se extraerán para cada campo del sistema en las primeras filas de ejemplo.
            La normalización definitiva se aplica al generar la vista previa.
          </Typography>

          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 200, bgcolor: "#fbfcfd" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {SYSTEM_FIELDS_CONFIG.map((field) => (
                    <TableCell key={field.key} sx={{ fontWeight: 600, backgroundColor: "#eef2f6" }}>
                      {field.label} {field.required ? "*" : ""}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sampleRows.map((row, idx) => (
                  <TableRow key={idx}>
                    {SYSTEM_FIELDS_CONFIG.map((field) => {
                      const colHeader = mapping[field.key];
                      const val = colHeader ? row[colHeader] : null;
                      return (
                        <TableCell key={field.key}>
                          {val !== undefined && val !== null && String(val).trim() !== "" ? (
                            String(val)
                          ) : (
                            <Typography variant="caption" color="text.disabled">
                              (sin asignar)
                            </Typography>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
}
